import React, { useState, useEffect, useRef } from 'react';
import { Loader2, ChevronUp } from 'lucide-react';

/**
 * Minimised scan indicator.
 *
 * Shown once the user dismisses the full-screen scan loader. The scan runs
 * server-side and reports over a socket, so blocking the whole app while it
 * finishes was always a UI choice rather than a requirement — this is what the
 * user sees instead.
 *
 * Deliberately small and corner-anchored: it has to stay legible for the
 * minutes a deep scan can take without getting in the way of the page.
 *
 * Props:
 *   isVisible (bool)
 *   title     (string — what is running, e.g. "Fetching jobs")
 *   startedAt (ms epoch — optional). Elapsed time is measured from this, so the
 *     pill and the expanded loader always agree and neither restarts when the
 *     user toggles between them.
 *   message   (string — latest progress line, if the caller has one)
 *   onExpand  (fn — reopen the full loader)
 *
 * Deliberately has NO close button. The work is server-side and cannot be
 * stopped from here, and an X on a progress indicator reads as "cancel". Hiding
 * it would leave the user with no way to tell a running job from a finished one.
 * The pill disappears on its own when the work completes.
 */

const formatElapsed = (seconds) => {
    if (seconds < 60) return `${seconds}s`;
    const m = Math.floor(seconds / 60);
    const rem = seconds % 60;
    return rem === 0 ? `${m}m` : `${m}m ${rem}s`;
};

const ScanProgressPill = ({ isVisible, title, message, onExpand, startedAt = null }) => {
    const [now, setNow] = useState(() => Date.now());
    const fallbackStartRef = useRef(null);

    useEffect(() => {
        if (!isVisible) { fallbackStartRef.current = null; return; }
        if (fallbackStartRef.current == null) fallbackStartRef.current = Date.now();
        setNow(Date.now());
        const id = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(id);
    }, [isVisible]);

    if (!isVisible) return null;

    const effectiveStart = startedAt || fallbackStartRef.current || now;
    const elapsed = Math.max(0, Math.floor((now - effectiveStart) / 1000));

    return (
        <div
            className="fixed z-40 font-dmsans"
            style={{ right: 24, bottom: 92 }}
            role="status"
            aria-live="polite"
        >
            <div
                className="flex items-center gap-3 rounded-2xl border border-[#E6E8EE] bg-white/95 backdrop-blur"
                style={{ padding: '12px 14px', boxShadow: '0 12px 32px rgba(15,23,42,.14)', maxWidth: 340 }}
            >
                <Loader2 className="w-4 h-4 shrink-0 animate-spin text-[#3F57E0]" />

                <div className="min-w-0">
                    <div className="text-[12.5px] font-semibold text-[#0F172A] truncate">
                        {title || 'Scanning for hiring signals'}
                    </div>
                    <div className="text-[11px] text-[#64748B] truncate">
                        {message || 'Working…'} · {formatElapsed(elapsed)}
                    </div>
                </div>

                <button
                    type="button"
                    onClick={onExpand}
                    title="Show details"
                    aria-label="Show details"
                    className="ml-1 grid h-7 w-7 shrink-0 place-items-center rounded-lg text-[#64748B] transition-colors hover:bg-[#F4F5F8] hover:text-[#0F172A]"
                >
                    <ChevronUp className="w-3.5 h-3.5" />
                </button>
            </div>
        </div>
    );
};

export default ScanProgressPill;
